<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
            <li class="pr-3"><a class="cursor-pointer">Operational Clarity</a></li>
            <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
            <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Digital Forms</a></li>
        </ul>

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Manage Digital Forms</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewDigitalForm" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Form</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="digitalForms"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['name', 'uniqueId']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Name or Unique ID"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No digital forms found.
                        </template>

                        <template #loading>
                            Loading digital forms. Please wait.
                        </template>

                        <Column
                            field="selectAnOption"
                            header=""
                            :headerStyle="{ width: '2rem' }"
                            :bodyStyle="{ width: '2rem', padding: '0.25rem' }">
                            <template #body="{ data, index }">
                                <Button icon="pi pi-ellipsis-v" class="p-button-sm p-button-text p-button-rounded m-0"
                                        @click="$refs['menu' + index].toggle($event)"/>
                                <Menu
                                    :ref="'menu' + index"
                                    :model="getFilteredItems(data)"
                                    :popup="true"/>
                            </template>
                        </Column>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column v-if="col.field === 'status'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Status</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Inactive</span>
                                    <span v-if="data.status === 'A'"
                                          :class="'status-badge status-' + data.status">Active</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'type'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Type</span>
                                    <span v-if="data.type === 'C'"
                                          :class="'type-badge status-' + data.type">Client</span>
                                    <span v-if="data.type === 'P'"
                                          :class="'type-badge status-' + data.type">Prospect</span>
                                    <span v-if="data.type === 'G'"
                                          :class="'type-badge status-' + data.type">General</span>
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Digital Form {{ this.digitalForm.name }}</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callReset" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Go Back</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div class="field">
                        <label for="uniqueLink">Unique Link</label>
                        <InputText id="uniqueLink" type="text" :disabled="true" v-model.trim="uniqueLink"/>
                    </div>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="digitalForm.name" required="true"/>
                        <small v-if="!digitalForm.name" class="p-error">Name is required.</small>
                    </div>

                    <div class="field">
                        <label for="uniqueId">Unique ID</label>
                        <InputText id="uniqueId" v-model.trim="digitalForm.uniqueId" required="true"/>
                        <small v-if="!digitalForm.uniqueId" class="p-error">Unique ID is required.</small>
                        <small v-if="digitalForm.uniqueId && !isAlphanumericUpdateDigitalForm" class="p-error">Unique ID
                            must be alphanumeric.</small>
                    </div>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="digitalForm.type" :options="formTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Type" required/>
                        <small v-if="!digitalForm.type" class="p-error">Type is required.</small>
                    </div>

                    <div class="field">
                        <label for="logo">Logo</label>
                        <Editor v-model="digitalForm.logo" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                    <button class="ql-image"></button>
                                </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="openingTerms">Opening Terms</label>
                        <Editor v-model="digitalForm.openingTerms" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                  <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                  <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                  <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                  <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                  <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                  <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                  <button class="ql-list" value="bullet" v-tooltip.bottom="'Bulleted List'"></button>
                                  <button class="ql-list" value="ordered" v-tooltip.bottom="'Ordered List'"></button>
                                  <button class="ql-indent" value="-1" v-tooltip.bottom="'Indent -1'"></button>
                                  <button class="ql-indent" value="+1" v-tooltip.bottom="'Indent +1'"></button>
                                </span>
                            </template>
                        </Editor>
                        <small v-if="!digitalForm.openingTerms" class="p-error">Opening Terms are required.</small>
                    </div>

                    <div class="field">
                        <label for="closingTerms">Closing Terms</label>
                        <Editor v-model="digitalForm.closingTerms" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                  <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                  <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                  <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                  <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                  <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                  <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                  <button class="ql-list" value="bullet" v-tooltip.bottom="'Bulleted List'"></button>
                                  <button class="ql-list" value="ordered" v-tooltip.bottom="'Ordered List'"></button>
                                  <button class="ql-indent" value="-1" v-tooltip.bottom="'Indent -1'"></button>
                                  <button class="ql-indent" value="+1" v-tooltip.bottom="'Indent +1'"></button>
                                </span>
                            </template>
                        </Editor>
                        <small v-if="!digitalForm.closingTerms" class="p-error">Closing Terms are required.</small>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" :loading="submitted"
                            :disabled="!allFieldsFilledUpdateDigitalForm" @click="updateDigitalForm"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Digital Form {{ this.digitalForm.name }}</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callReset" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Go Back</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Digital Form?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" :loading="submitted" @click="deleteDigitalForm"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>New Form</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callReset" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Go Back</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div class="field">
                        <span>
                           <b>Fields that can be used to create a client :</b>
                            <br/>
                             "First Name", "Last Name", "Name", "Email", "Mobile",
                            "Preferred Name", "ID Number", "Date of Birth", "Gender", "Address Line 1",
                            "Address Line 2", "Region", "State", "Province", "Country", "Postal Code", "Zip Code",
                            "Medical Aid Name", "Medical Aid Plan", "Medical Aid Number",
                            "Main Member Code", "Main Member ID Number", "Main Member Initials",
                            "Main Member First Name", "Main Member Last Name",
                            "Dependant Code", "Dependant ID Number", "Dependant Initials",
                            "Dependant First Name", "Dependant Last Name"
                        </span>
                    </div>

                    <div class="field">
                        &nbsp;
                    </div>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="newDigitalForm.name" required="true"/>
                        <small v-if="!newDigitalForm.name" class="p-error">Name is required.</small>
                    </div>

                    <div class="field">
                        <label for="uniqueId">Unique ID</label>
                        <InputText id="uniqueId" v-model.trim="newDigitalForm.uniqueId" required="true"/>
                        <small v-if="!newDigitalForm.uniqueId" class="p-error">Unique ID is required.</small>
                        <small v-if="newDigitalForm.uniqueId && !isAlphanumericNewDigitalForm" class="p-error">Unique ID
                            must be alphanumeric.</small>
                    </div>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="newDigitalForm.type" :options="formTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Type" required/>
                        <small v-if="!newDigitalForm.type" class="p-error">Type is required.</small>
                    </div>

                    <div class="field">
                        <label for="logo">Logo</label>
                        <Editor v-model="newDigitalForm.logo" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                    <button class="ql-image"></button>
                                </span>
                            </template>
                        </Editor>
                    </div>

                    <div class="field">
                        <label for="openingTerms">Opening Terms</label>
                        <Editor v-model="newDigitalForm.openingTerms" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                  <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                  <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                  <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                  <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                  <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                  <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                  <button class="ql-list" value="bullet" v-tooltip.bottom="'Bulleted List'"></button>
                                  <button class="ql-list" value="ordered" v-tooltip.bottom="'Ordered List'"></button>
                                  <button class="ql-indent" value="-1" v-tooltip.bottom="'Indent -1'"></button>
                                  <button class="ql-indent" value="+1" v-tooltip.bottom="'Indent +1'"></button>
                                </span>
                            </template>
                        </Editor>
                        <small v-if="!newDigitalForm.openingTerms" class="p-error">Opening Terms are required.</small>
                    </div>

                    <div class="field">
                        <label for="closingTerms">Closing Terms</label>
                        <Editor v-model="newDigitalForm.closingTerms" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                  <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                  <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                  <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                  <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                  <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                  <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                  <button class="ql-list" value="bullet" v-tooltip.bottom="'Bulleted List'"></button>
                                  <button class="ql-list" value="ordered" v-tooltip.bottom="'Ordered List'"></button>
                                  <button class="ql-indent" value="-1" v-tooltip.bottom="'Indent -1'"></button>
                                  <button class="ql-indent" value="+1" v-tooltip.bottom="'Indent +1'"></button>
                                </span>
                            </template>
                        </Editor>
                        <small v-if="!newDigitalForm.closingTerms" class="p-error">Closing Terms are required.</small>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" :loading="submitted"
                            :disabled="!allFieldsFilledNewDigitalForm" @click="createDigitalForm"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 5" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>View Components for Digital Form {{ this.digitalForm.name }}</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callReset" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Go Back</span>
                                </a>
                            </li>
                            <li>
                                <a @click="callNewDigitalFormComponent" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Form Component</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div style="height: 0.5rem;"></div>

                    <div class="field">
                        <span>
                           <b>Fields that can be used to create a client :</b>
                            <br/>
                             "First Name", "Last Name", "Name", "Email", "Mobile",
                            "Preferred Name", "ID Number", "Date of Birth", "Gender", "Address Line 1",
                            "Address Line 2", "Region", "State", "Province", "Country", "Postal Code", "Zip Code",
                            "Medical Aid Name", "Medical Aid Plan", "Medical Aid Number",
                            "Main Member Code", "Main Member ID Number", "Main Member Initials",
                            "Main Member First Name", "Main Member Last Name",
                            "Dependant Code", "Dependant ID Number", "Dependant Initials",
                            "Dependant First Name", "Dependant Last Name"
                        </span>
                    </div>

                    <div style="height: 1rem;"></div>


                    <DataTable
                        :value="digitalFormComponents"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['name']">

                        <template #header>
                            <div class="p-grid">
                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Name"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No digital form components found.
                        </template>

                        <template #loading>
                            Loading digital form components. Please wait.
                        </template>

                        <Column
                            field="selectAnOption"
                            header=""
                            :headerStyle="{ width: '2rem' }"
                            :bodyStyle="{ width: '2rem', padding: '0.25rem' }">
                            <template #body="{ data, index }">
                                <Button icon="pi pi-ellipsis-v" class="p-button-sm p-button-text p-button-rounded m-0"
                                        @click="$refs['componentmenu' + index].toggle($event)"/>
                                <Menu
                                    :ref="'componentmenu' + index"
                                    :model="getFilteredItemsComponents(data)"
                                    :popup="true"/>
                            </template>
                        </Column>

                        <Column field="name" header="Name" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Name</span>
                                {{ data.name }}
                            </template>
                        </Column>

                        <Column field="priority" header="Priority" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Priority</span>
                                {{ data.priority }}
                            </template>
                        </Column>

                        <Column field="type" header="Type" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Type</span>
                                <span v-if="data.type === 'S'"
                                      :class="'type-badge status-' + data.type">String</span>
                                <span v-if="data.type === 'B'"
                                      :class="'type-badge status-' + data.type">Boolean</span>
                                <span v-if="data.type === 'N'"
                                      :class="'type-badge status-' + data.type">Number</span>
                                <span v-if="data.type === 'D'"
                                      :class="'type-badge status-' + data.type">Datetime</span>
                                <span v-if="data.type === 'H'"
                                      :class="'type-badge status-' + data.type">Header</span>
                                <span v-if="data.type === 'L'"
                                      :class="'type-badge status-' + data.type">Line Break</span>
                                <span v-if="data.type === 'T'"
                                      :class="'type-badge status-' + data.type">Text</span>
                                <span v-if="data.type === 'G'"
                                      :class="'type-badge status-' + data.type">Signature</span>
                                <span v-if="data.type === 'M'"
                                      :class="'type-badge status-' + data.type">List</span>
                            </template>
                        </Column>

                        <Column field="required" header="Required" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Required</span>
                                <span v-if="data.required === 'Y'"
                                      :class="'required-badge status-' + data.required">Yes</span>
                                <span v-if="data.required === 'N'"
                                      :class="'required-badge status-' + data.required">No</span>
                            </template>
                        </Column>

                        <Column field="status" header="Status" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Status</span>
                                <span v-if="data.status === 'I'"
                                      :class="'status-badge status-' + data.status">Inactive</span>
                                <span v-if="data.status === 'A'"
                                      :class="'status-badge status-' + data.status">Active</span>
                            </template>
                        </Column>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 6" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>New Form Component for {{ this.digitalForm.name }}</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callResetComponents" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Go Back</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="newDigitalFormComponent.name" required="true"/>
                        <small v-if="!newDigitalFormComponent.name" class="p-error">Name is required.</small>
                    </div>

                    <div class="field">
                        <label for="priority">Priority</label>
                        <InputText id="priority" v-model.trim="newDigitalFormComponent.priority" required="true"/>
                        <small v-if="!newDigitalFormComponent.priority" class="p-error">Priority is required.</small>
                        <small v-if="newDigitalFormComponent.priority && !isNumericNewDigitalFormComponent"
                               class="p-error">Priority must be numeric.</small>
                    </div>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="newDigitalFormComponent.type" :options="formComponentTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Type" required/>
                        <small v-if="!newDigitalFormComponent.type" class="p-error">Type is required.</small>
                        <small v-if="newDigitalFormComponent.type === 'G' && isSignatureComponentPresent"
                               class="p-error">
                            A Signature already exists and cannot be added again.
                        </small>
                    </div>

                    <div
                        v-show="(newDigitalFormComponent.type != 'H') && (newDigitalFormComponent.type != 'L') && (newDigitalFormComponent.type != 'T') "
                        class="field">
                        <label for="type">Required</label>
                        <Dropdown v-model="newDigitalFormComponent.required" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select an Option" required/>
                        <small v-if="!newDigitalFormComponent.required" class="p-error">Option is required.</small>
                    </div>

                    <div v-show="newDigitalFormComponent.type === 'T'" class="field">
                        <label for="openingTerms">Value for Text</label>
                        <Editor v-model="newDigitalFormComponent.extendedValue" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                  <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                  <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                  <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                  <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                  <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                  <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                  <button class="ql-list" value="bullet" v-tooltip.bottom="'Bulleted List'"></button>
                                  <button class="ql-list" value="ordered" v-tooltip.bottom="'Ordered List'"></button>
                                  <button class="ql-indent" value="-1" v-tooltip.bottom="'Indent -1'"></button>
                                  <button class="ql-indent" value="+1" v-tooltip.bottom="'Indent +1'"></button>
                                </span>
                            </template>
                        </Editor>
                        <small v-if="!newDigitalFormComponent.extendedValue" class="p-error">Value is required.</small>
                    </div>

                    <div v-show="newDigitalFormComponent.type === 'M'" class="field">
                        <label for="extendedValue">Value for List (separated by a comma)</label>
                        <InputText id="extendedValue" v-model.trim="newDigitalFormComponent.extendedValue"
                                   required="true"/>
                        <small v-if="!newDigitalFormComponent.extendedValue" class="p-error">Value is required.</small>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" :loading="submitted"
                            :disabled="!allFieldsFilledNewDigitalFormComponent"
                            @click="createDigitalFormComponent"></Button>

                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callResetComponents"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 7" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Form Component for {{ this.digitalForm.name }}</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callResetComponents" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Go Back</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div class="field">
                        <label for="name">Name</label>
                        <InputText id="name" v-model.trim="digitalFormComponent.name" required="true"/>
                        <small v-if="!digitalFormComponent.name" class="p-error">Name is required.</small>
                    </div>

                    <div class="field">
                        <label for="priority">Priority</label>
                        <InputText id="priority" v-model.trim="digitalFormComponent.priority" required="true"/>
                        <small v-if="!digitalFormComponent.priority" class="p-error">Priority is required.</small>
                        <small v-if="digitalFormComponent.priority && !isNumericUpdateDigitalFormComponent"
                               class="p-error">Priority must be numeric.</small>
                    </div>

                    <div class="field">
                        <label for="type">Type</label>
                        <Dropdown v-model="digitalFormComponent.type" :options="formComponentTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Type" required/>
                        <small v-if="!digitalFormComponent.type" class="p-error">Type is required.</small>
                    </div>

                    <div
                        v-if="(digitalFormComponent.type != 'H') && (digitalFormComponent.type != 'L') && (digitalFormComponent.type != 'T')"
                        class="field">
                        <label for="type">Required</label>
                        <Dropdown v-model="digitalFormComponent.required" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select an Option" required/>
                        <small v-if="!digitalFormComponent.required" class="p-error">Option is required.</small>
                    </div>

                    <div v-show="digitalFormComponent.type === 'T'" class="field">
                        <label for="openingTerms">Value for Text</label>
                        <Editor v-model="digitalFormComponent.extendedValue" editorStyle="height: 250px">
                            <template v-slot:toolbar>
                                <span class="ql-formats">
                                  <button class="ql-bold" v-tooltip.bottom="'Bold'"></button>
                                  <button class="ql-italic" v-tooltip.bottom="'Italic'"></button>
                                  <button class="ql-underline" v-tooltip.bottom="'Underline'"></button>
                                  <button class="ql-strike" v-tooltip.bottom="'Strike'"></button>
                                  <button class="ql-color" v-tooltip.bottom="'Color'"></button>
                                  <button class="ql-background" v-tooltip.bottom="'Color'"></button>
                                  <button class="ql-list" value="bullet" v-tooltip.bottom="'Bulleted List'"></button>
                                  <button class="ql-list" value="ordered" v-tooltip.bottom="'Ordered List'"></button>
                                  <button class="ql-indent" value="-1" v-tooltip.bottom="'Indent -1'"></button>
                                  <button class="ql-indent" value="+1" v-tooltip.bottom="'Indent +1'"></button>
                                </span>
                            </template>
                        </Editor>
                        <small v-if="!digitalFormComponent.extendedValue" class="p-error">Value is required.</small>
                    </div>

                    <div v-show="digitalFormComponent.type === 'M'" class="field">
                        <label for="extendedValue">Value for List (separated by a comma)</label>
                        <InputText id="extendedValue" v-model.trim="digitalFormComponent.extendedValue"
                                   required="true"/>
                        <small v-if="!digitalFormComponent.extendedValue" class="p-error">Value is required.</small>
                    </div>

                    <div class="field">
                        <label for="type">Status</label>
                        <Dropdown v-model="digitalFormComponent.status" :options="statusTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select an Option" required/>
                        <small v-if="!digitalFormComponent.status" class="p-error">Option is required.</small>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" :loading="submitted"
                            :disabled="!allFieldsFilledUpdateDigitalFormComponent"
                            @click="updateDigitalFormComponent"></Button>

                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callResetComponents"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 8" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Form Component for {{ this.digitalForm.name }}</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callResetComponents" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Go Back</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Digital Form
                            Component?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" :loading="submitted"
                            @click="deleteDigitalFormComponent"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callResetComponents"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 9" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>View Responses for Digital Form {{ this.digitalForm.name }}</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callReset" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Go Back</span>
                                </a>
                            </li>
                            <li>
                                <a @click="callNewDigitalFormComponent" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Form Component</span>
                                </a>
                            </li>
                        </ul>
                    </div>


                    <DataTable
                        :value="digitalFormUniqueResponses"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['name']">

                        <template #header>
                            <div class="p-grid">
                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Name"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No digital form responses found.
                        </template>

                        <template #loading>
                            Loading digital form responses. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 10%">
                            <template #body="{ data }">
                                <Button v-tooltip="'View'" icon="pi pi-eye"
                                        class="p-button-rounded p-button-success m-1"
                                        :loading="submitted" @click="callViewDigitalFormResponse(data)"/>
                                <Button v-tooltip="'Delete'" icon="pi pi-trash"
                                        class="p-button-rounded p-button-danger m-1"
                                        :loading="submitted" @click="callDeleteDigitalFormResponse(data)"/>
                            </template>
                        </Column>

                        <Column field="uniqueId" header="Unique ID" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Unique ID</span>
                                {{ data.uniqueId }}
                            </template>
                        </Column>

                        <Column field="ipAddress" header="IP Address" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">IP Address</span>
                                {{ data.ipAddress }}
                            </template>
                        </Column>

                        <Column field="created" header="Created" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Created</span>
                                {{ data.created }}
                            </template>
                        </Column>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 10" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>View Response for Digital Form {{ this.digitalForm.name }}</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callReset" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Go Back</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <h3>{{ this.digitalForm.name }}</h3>

                    <div class="field">
                        <p v-html="this.digitalForm.openingTerms"/>
                    </div>

                    <div class="field">
                        <p>&nbsp;</p>
                    </div>

                    <div v-for="component in digitalForm.formComponent" :key="component.id" class="field">

                        <!-- Wrapping element with the v-if -->
                        <div v-if="( (component.type === 'S') || (component.type === 'N') || (component.type === 'B') ||
                                     (component.type === 'D') || (component.type === 'M') )">
                            <label :for="component.name"><h4>{{ component.name }}</h4></label>
                            <div v-if="responsesByComponent[component.id]">
                                <span v-for="(response, index) in responsesByComponent[component.id]" :key="index"
                                      style="display: inline;">
                                    {{ response }}<span
                                    v-if="index < responsesByComponent[component.id].length - 1">, </span>
                                </span>
                            </div>
                            <span v-else>No responses available</span> <!-- Fallback message -->
                        </div>

                        <!-- Wrapping element with the v-else-if -->
                        <div v-else-if="component.type === 'H'" class="field">
                            <h3>{{ component.name }}</h3>
                        </div>

                        <!-- Wrapping element with the v-else-if -->
                        <div v-else-if="component.type === 'L'" class="field">
                            <p>&nbsp;</p>
                        </div>

                        <!-- Wrapping element with the v-else-if -->
                        <div v-else-if="component.type === 'T' && component.extendedValue !== null" class="field">
                            <p v-html="component.extendedValue"/>
                        </div>

                        <!--                        <div v-else-if="component.type === 'M' && component.extendedValue !== null" class="field">-->
                        <!--                            <span>{{ component.value }}</span>-->
                        <!--                        </div>-->

                        <!-- Wrapping element with the v-else-if -->
                        <div v-else-if="component.type === 'G'">
                            <label :for="component.name"><h4>{{ component.name }}</h4></label>
                            <div v-if="responsesByComponent[component.id]">
                                <div v-for="(response, index) in responsesByComponent[component.id]" :key="index"
                                     class="field">
                                    <img v-if="response.startsWith('data:image/png;base64,')" :src="response"
                                         alt="Signature"/>
                                    <span v-else>{{ response }}</span> <!-- Fallback if the response is not an image -->
                                </div>
                            </div>
                            <span v-else>No signature available</span> <!-- Fallback message -->
                        </div>

                        <!-- Final else condition -->
                        <p v-else>Unsupported component type: {{ component.type }}</p>

                    </div>

                    <div class="field">
                        <p>&nbsp;</p>
                    </div>

                    <div class="field">
                        <p>&nbsp;</p>
                    </div>

                    <div class="field">
                        <p v-html="this.digitalForm.closingTerms"/>
                    </div>

                    <div class="field">
                        <p>&nbsp;</p>
                    </div>

                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callResetResponses"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 11" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Form Response for {{ this.digitalForm.name }}</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callReset" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Go Back</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Digital Form
                            Response?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" :loading="submitted"
                            @click="deleteDigitalFormResponse"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callResetResponses"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import CompanyService from "@/service/CompanyService";
import numeral from "numeral";
import DigitalForm from "@/service/DigitalFormService";
import DigitalFormService from "@/service/DigitalFormService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();
    },
    async mounted() {
        try {
            const userPingResponse = await UserService.getUserPing();
            console.log("[PmOverviewDashboard][mounted] System is active.");
            console.log("[PmOverviewDashboard][mounted] Data = ", userPingResponse);
        } catch (e) {
            console.log("[PmOverviewDashboard][mounted] System is NOT active.");
            console.log("[PmOverviewDashboard][mounted] Error = ", e);
            this.userStore.clearUser();
            this.$router.push("login");
            return;
        }

        if (!this.userStore.user.id) {
            console.log("[PmOverviewDashboard][mounted] User is NOT defined.");
            this.userStore.clearUser();
            this.$router.push('login');
            return;
        }

        await this.loadCompanyContext();

    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            loading: true,
            submitted: false,
            digitalForm: {},
            newDigitalForm: {},
            digitalForms: null,
            originalDigitalForms: null,
            digitalFormComponents: null,
            digitalFormComponent: {},
            newDigitalFormComponent: {},
            digitalFormResponses: null,
            digitalFormUniqueResponses: null,
            digitalFormUniqueResponse: {},
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ],
            formTypes: [
                {name: 'Client', code: 'C'},
                {name: 'Prospect', code: 'P'},
                {name: 'General', code: 'G'},
            ],
            formComponentTypes: [
                {name: 'String', code: 'S'},
                {name: 'Boolean', code: 'B'},
                {name: 'Number', code: 'N'},
                {name: 'Date', code: 'D'},
                {name: 'List', code: 'M'},
                {name: 'Header', code: 'H'},
                {name: 'Line Break', code: 'L'},
                {name: 'Text', code: 'T'},
                {name: 'Signature', code: 'G'}
            ],
            healthInsuranceSubmissionMethod: [
                {name: 'Email', code: 'E'},
                {name: 'Electronic Data Interchange (EDI)', code: 'D'}
            ],
            recurringTypes: [
                {name: 'None', code: 'N'},
                {name: 'Daily', code: 'D'},
                {name: 'Weekly', code: 'W'},
                {name: 'Bi-Weekly', code: 'B'},
                {name: 'Monthly', code: 'M'}
            ]
        }
    },
    watch: {
        '$route.query.tab'(newVal) {
            if (newVal === "1") {
                this.activeTab = 1;
                this.$router.replace({ path: this.$route.path });
            } else if (newVal === "2") {
                this.callNewDigitalForm();
                this.$router.replace({ path: this.$route.path }); // remove ?new=1
            }
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    computed: {
        allFieldsFilledNewDigitalForm() {
            return this.newDigitalForm.name &&
                this.newDigitalForm.uniqueId &&
                this.newDigitalForm.type &&
                this.newDigitalForm.openingTerms &&
                this.newDigitalForm.closingTerms;
        },
        allFieldsFilledUpdateDigitalForm() {
            return this.digitalForm.name &&
                this.digitalForm.uniqueId &&
                this.digitalForm.type &&
                this.digitalForm.openingTerms &&
                this.digitalForm.closingTerms;
        },
        allFieldsFilledNewDigitalFormComponent() {
            // return this.newDigitalFormComponent.name &&
            //     this.newDigitalFormComponent.required &&
            //     this.newDigitalFormComponent.type &&
            //     this.newDigitalFormComponent.priority;

            const {type, name, required, priority} = this.newDigitalFormComponent;

            // Exclude types 'H' and 'L' from the required fields check
            if (type === 'H' || type === 'L') {
                return true; // Consider all fields filled if the type is 'H' or 'L'
            }

            // For other types, check if all required fields are filled
            return name && required && type && priority;
        },
        allFieldsFilledUpdateDigitalFormComponent() {
            return this.digitalFormComponent.name &&
                this.digitalFormComponent.required &&
                this.digitalFormComponent.type &&
                this.digitalFormComponent.priority;
        },
        isAlphanumericNewDigitalForm() {
            return /^[a-zA-Z0-9]*$/.test(this.newDigitalForm.uniqueId);
        },
        isAlphanumericUpdateDigitalForm() {
            return /^[a-zA-Z0-9-]*$/.test(this.digitalForm.uniqueId);
        },
        isNumericNewDigitalFormComponent() {
            return /^[0-9]*$/.test(this.newDigitalFormComponent.priority);
        },
        isNumericUpdateDigitalFormComponent() {
            return /^[0-9]*$/.test(this.digitalFormComponent.priority);
        },
        uniqueLink() {
            return `https://app.isoevolve.com/#/digitalform?company=${this.profile.companyId}&form=${this.digitalForm.uniqueId}`;
        },
        responsesByComponent() {
            const responseMap = {};

            this.digitalForm.responses.forEach(response => {
                // Check if the response's uniqueId matches the digitalFormUniqueResponse's uniqueId
                if (response.uniqueId === this.digitalFormUniqueResponse.uniqueId) {
                    if (!responseMap[response.formComponentId]) {
                        responseMap[response.formComponentId] = [];
                    }
                    responseMap[response.formComponentId].push(response.response);
                }
            });

            return responseMap;
        },
        isSignatureComponentPresent() {
            if (!Array.isArray(this.digitalForm.formComponent)) {
                console.error('formComponent is not an array');
                return false;
            }
            return this.digitalForm.formComponent.some(component => component.type === 'G');
        },
        canCreateNewComponent() {
            return this.allFieldsFilledNewDigitalFormComponent &&
                (!(this.newDigitalFormComponent.type === 'G' && this.isSignatureComponentPresent()));
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                try {
                    // Attempt to get user data
                    const userData = await UserService.getUser(this.userStore.user.id);
                    this.profile = userData.data;
                } catch (e) {
                    console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                    this.userStore.clearUser();
                    this.$router.push('login');
                    return;
                }

                this.profile.companyId = this.userStore.activeCompanyId;

                const [userConfigurationData, companyConfigurationData, digitalFormsData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id),
                    CompanyService.getCompanyConfiguration(this.profile.companyId),
                    DigitalFormService.getDigitalForms(this.profile.companyId)
                ]);

                this.userConfiguration = userConfigurationData.data;
                this.companyConfiguration = companyConfigurationData.data;

                this.digitalForms = digitalFormsData.data;
                this.originalDigitalForms = [...this.digitalForms];

                this.loading = false;

            } catch (e) {
                console.error("[DigitalForms][mounted] Error fetching additional data = ", e);
            }

            console.log("[DigitalForms][mounted] this.digitalForms = ", this.digitalForms);
            console.log("[DigitalForms][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[DigitalForms][mounted] this.companyConfiguration = ", this.companyConfiguration);

            this.columnsDefault = [
                {field: 'name', header: 'Name', sortable: true},
                {field: 'uniqueId', header: 'Unique ID', sortable: true},
                {field: 'type', header: 'Type', sortable: true},
                {field: 'status', header: 'Status', sortable: true}
            ];

            this.columnsFullList = [
                {field: 'name', header: 'Name', sortable: true},
                {field: 'uniqueId', header: 'Unique ID', sortable: true},
                {field: 'type', header: 'Type', sortable: true},
                {field: 'status', header: 'Status', sortable: true}
            ];

            this.selectedColumns = this.columnsDefault;

            this.standardFilters = [
                {field: 'active', header: 'Active'},
                {field: 'inactive', header: 'Inactive'}
            ];

            if (this.$route.query.new === "1") {
                this.callNewDigitalForm();
            }
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'name': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'uniqueId': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        restoreToOriginal() {
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
        },
        callResetComponents() {
            this.activeTab = 5;
        },
        callResetResponses() {
            this.activeTab = 9;
        },
        callNewDigitalForm() {
            this.newDigitalForm = {};
            this.newDigitalForm.type = 'G';
            this.activeTab = 4;
        },
        callHelp() {
            console.log('[ConfigurationBilling][callHelp] Start.');
        },
        callSupport() {
            console.log('[ConfigurationBilling][callSupport] Start.');
        },
        async callViewDigitalForm(data) {
            this.digitalForm = data;

            console.log('[DigitalForms][callViewDigitalForm] this.digitalForm = ', this.digitalForm);

            try {

                const [formUrlData] = await Promise.all([
                    DigitalFormService.getDigitalFormLink(this.digitalForm.id)
                ]);

                const filename = formUrlData.data;

                console.log('[DigitalForms][callViewDigitalForm] filename = ', filename);

                if (filename) {
                    window.open(
                        filename,
                        '_blank',
                        'noopener,noreferrer'
                    );
                }
            } catch (error) {
                console.error('[DigitalForms][callViewDigitalForm] Error:', error);
            }
        },
        callUpdateDigitalForm(data) {
            this.activeTab = 2;
            this.digitalForm = data;
        },
        callDeleteDigitalForm(data) {
            this.activeTab = 3;
            this.confirmationResponse = 'N';
            this.digitalForm = data;
        },
        callNewDigitalFormComponent() {
            this.newDigitalFormComponent = {};
            this.newDigitalFormComponent.required = 'Y';
            this.newDigitalFormComponent.type = 'S';
            this.newDigitalFormComponent.status = 'A';
            this.activeTab = 6;
        },
        callUpdateDigitalFormComponent(data) {
            this.activeTab = 7;
            this.digitalFormComponent = data;
        },
        callDeleteDigitalFormComponent(data) {
            this.activeTab = 8;
            this.confirmationResponse = 'N';
            this.digitalFormComponent = data;
        },
        async callViewDigitalForms() {
            this.activeTab = 1;
        },
        async callViewDigitalFormComponents(data) {
            this.digitalForm = data;
            this.digitalFormComponents = data.formComponent;
            this.activeTab = 5;

            console.log("[DigitalForms][callViewDigitalFormComponents] this.digitalForm = ", this.digitalForm);
            console.log("[DigitalForms][callViewDigitalFormComponents] this.digitalFormComponents = ", this.digitalFormComponents);
        },
        async callViewDigitalFormResponses(data) {
            this.digitalForm = data;
            this.activeTab = 9;

            console.log("[DigitalForms][callViewDigitalFormComponents] this.digitalForm = ", this.digitalForm);

            const [resultsData] = await Promise.all([
                DigitalFormService.getUniqueResponses(this.digitalForm.id)
            ]);

            // this.digitalFormUniqueResponses = resultsData.data.map(response => {
            //     return {
            //         ...response,
            //         uniqueId: response.uniqueId.toString() // Convert the uniqueId to string
            //     };
            // });

            this.digitalFormUniqueResponses = resultsData.data;

            console.log("[DigitalForms][callViewDigitalFormComponents] this.digitalFormUniqueResponses = ", this.digitalFormUniqueResponses);
        },
        callViewDigitalFormResponse(data) {
            this.digitalFormUniqueResponse = data;
            this.activeTab = 10;

            console.log("[DigitalForms][callViewDigitalFormResponse] this.digitalForm = ", this.digitalForm);
            console.log("[DigitalForms][callViewDigitalFormResponse] this.digitalFormComponents = ", this.digitalFormComponents);
            console.log("[DigitalForms][callViewDigitalFormResponse] this.digitalFormUniqueResponse = ", this.digitalFormUniqueResponse);

        },
        callDeleteDigitalFormResponse(data) {
            this.digitalFormUniqueResponse = data;
            this.activeTab = 11;
            this.confirmationResponse = 'N';

            console.log("[DigitalForms][callViewDigitalFormResponse] this.digitalForm = ", this.digitalForm);
            console.log("[DigitalForms][callViewDigitalFormResponse] this.digitalFormComponents = ", this.digitalFormComponents);
            console.log("[DigitalForms][callViewDigitalFormResponse] this.digitalFormUniqueResponse = ", this.digitalFormUniqueResponse);

        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isActive = value.some(item => item.field === "active");
            const isInactive = value.some(item => item.field === "inactive");

            if (this.digitalForms !== null) {

                if (isActive) {
                    this.digitalForms = this.originalDigitalForms.filter(digitalForm => digitalForm.status === 'A');
                } else if (isInactive) {
                    this.digitalForms = this.originalDigitalForms.filter(digitalForm => digitalForm.status === 'I');
                } else {
                    this.digitalForms = this.originalDigitalForms;
                }

            } else {
                // Handle the case when auditTrails is null
                this.digitalForms = null;
            }
        },
        getFilteredItems(data) {
            const items = [];

            items.push({
                label: 'View Digital Form',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callViewDigitalForm(data);
                }
            });

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callUpdateDigitalForm(data);
                }
            });

            items.push({
                label: 'View Components',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callViewDigitalFormComponents(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDeleteDigitalForm(data);
                }
            });

            items.push({
                label: 'View Responses',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callViewDigitalFormResponses(data);
                }
            });

            return items;
        },
        getFilteredItemsComponents(data) {
            const items = [];

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callUpdateDigitalFormComponent(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDeleteDigitalFormComponent(data);
                }
            });

            return items;
        },
        chooseBlankSelection(data) {
            console.log('[DigitalForms][chooseBlankSelection] data = ', data);
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        },
        formatAmount(value) {
            if (value) {
                return numeral(String(value)).format('0,0.00');
            }
        },
        searchDiagnosticCodes(event) {
            console.log("[" + name + "][searchDiagnosticCodes] this.filteredDiagnosticCodes BEFORE = ", this.filteredDiagnosticCodes);
            console.log("[" + name + "][searchDiagnosticCodes] event.query.toLowerCase() = ", event.query.toLowerCase());

            this.icd10CodeService.getIcd10CodesSimple(event.query.toLowerCase())
                .then(data => {
                    this.filteredDiagnosticCodes = data.data;
                })
                .catch(e => {
                    console.log(e);
                });

            console.log("[" + name + "][searchDiagnosticCodes] this.filteredIcd10Codes AFTER = ", this.filteredDiagnosticCodes);

        },
        formatAsPercentage(number) {
            // Check if the input is a valid number
            if (isNaN(number)) {
                return "Invalid Number";
            }

            // Divide the number by 100 to convert it to a percentage
            const percentage = number / 100;

            // Use toLocaleString to format as a percentage with two decimal places
            return percentage.toLocaleString(undefined, {
                style: "percent",
                minimumFractionDigits: 2,
                maximumFractionDigits: 5,
            });
        },
        async createDigitalForm() {
            this.submitted = true;

            if (this.newDigitalForm.name) {

                try {
                    var newDigitalFormDto = {
                        name: this.newDigitalForm.name,
                        uniqueId: this.newDigitalForm.uniqueId,
                        type: this.newDigitalForm.type,
                        logo: this.newDigitalForm.logo,
                        openingTerms: this.newDigitalForm.openingTerms,
                        closingTerms: this.newDigitalForm.closingTerms,
                        status: 'A'
                    };

                    const response = await DigitalForm.createDigitalForm(this.profile.companyId, newDigitalFormDto);
                    console.log('[Forms][createDigitalForm] Response = ', response);

                    const digitalFormsData = await DigitalFormService.getDigitalForms(this.profile.companyId);
                    this.userStore.setDigitalForms(digitalFormsData.data);

                    this.digitalForms = this.userStore.digitalForms;
                    this.originalDigitalForms = this.userStore.digitalForms;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created digital form',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[DigitalForms][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create digital form. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async updateDigitalForm() {
            this.submitted = true;

            if (this.digitalForm.id) {

                try {
                    var newDigitalFormDto = {
                        name: this.digitalForm.name,
                        uniqueId: this.digitalForm.uniqueId,
                        type: this.digitalForm.type,
                        logo: this.digitalForm.logo,
                        openingTerms: this.digitalForm.openingTerms,
                        closingTerms: this.digitalForm.closingTerms,
                        status: this.digitalForm.status
                    };

                    const response = await DigitalForm.updateDigitalForm(this.digitalForm.id, newDigitalFormDto);
                    console.log('[Forms][createDigitalForm] Response = ', response);

                    const digitalFormsData = await DigitalFormService.getDigitalForms(this.profile.companyId);
                    this.userStore.setDigitalForms(digitalFormsData.data);

                    this.digitalForms = this.userStore.digitalForms;
                    this.originalDigitalForms = this.userStore.digitalForms;

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated digital form',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[DigitalForms][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update digital form. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async deleteDigitalForm() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.digitalForm.id) {

                    try {
                        const response = await DigitalForm.deleteDigitalForm(this.digitalForm.id);
                        console.log('[Forms][createDigitalForm] Response = ', response);

                        const digitalFormsData = await DigitalFormService.getDigitalForms(this.profile.companyId);
                        this.userStore.setDigitalForms(digitalFormsData.data);

                        this.digitalForms = this.userStore.digitalForms;
                        this.originalDigitalForms = this.userStore.digitalForms;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted digital form',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[DigitalForms][deletePhoneCall] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete digital form. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async createDigitalFormComponent() {
            this.submitted = true;

            if (this.newDigitalFormComponent.name) {

                try {
                    var digitalFormId = this.digitalForm.id;
                    var requiredValue = this.newDigitalFormComponent.required;

                    if ((this.newDigitalFormComponent.type === 'H') ||
                        (this.newDigitalFormComponent.type === 'L') ||
                        (this.newDigitalFormComponent.type === 'T') ||
                        (this.newDigitalFormComponent.type === 'B') ||
                        (this.newDigitalFormComponent.type === 'D')) {
                        requiredValue = 'N';
                    }


                    var digitalFormComponentDto = {
                        type: this.newDigitalFormComponent.type,
                        required: requiredValue,
                        priority: this.newDigitalFormComponent.priority,
                        name: this.newDigitalFormComponent.name,
                        extendedValue: this.newDigitalFormComponent.extendedValue,
                        status: 'A'
                    };

                    const response = await DigitalFormService.createDigitalFormComponent(this.digitalForm.id, digitalFormComponentDto);
                    console.log('[DigitalForms][createDigitalFormComponent] Response = ', response);

                    const digitalFormsData = await DigitalFormService.getDigitalForms(this.profile.companyId);
                    this.userStore.setDigitalForms(digitalFormsData.data);

                    this.digitalForms = this.userStore.digitalForms;
                    this.originalDigitalForms = this.userStore.digitalForms;
                    console.log('[DigitalForms][createDigitalFormComponent] this.digitalForms = ', this.digitalForms);

                    this.digitalForm = this.digitalForms.find(digitalForm => digitalForm.id === digitalFormId);
                    this.digitalFormComponents = this.digitalForm.formComponent;
                    console.log('[DigitalForms][createDigitalFormComponent] this.digitalForm = ', this.digitalForm);
                    console.log('[DigitalForms][createDigitalFormComponent] this.digitalFormComponents = ', this.digitalFormComponents);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created digital form component',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[DigitalForms][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create digital form component. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 5;
            this.submitted = false;
        },
        async updateDigitalFormComponent() {
            this.submitted = true;

            if (this.digitalFormComponent.name) {

                try {
                    console.log('[DigitalForms][updateDigitalFormComponent] this.digitalForm = ', this.digitalForm);
                    console.log('[DigitalForms][updateDigitalFormComponent] this.digitalFormComponent = ', this.digitalFormComponent);

                    var digitalFormId = this.digitalForm.id;
                    var requiredValue = this.digitalFormComponent.required;

                    if ((this.digitalFormComponent.type === 'H') ||
                        (this.digitalFormComponent.type === 'L') ||
                        (this.digitalFormComponent.type === 'T')) {
                        requiredValue = 'N';
                    }

                    var digitalFormComponentDto = {
                        type: this.digitalFormComponent.type,
                        required: requiredValue,
                        priority: this.digitalFormComponent.priority,
                        name: this.digitalFormComponent.name,
                        extendedValue: this.digitalFormComponent.extendedValue,
                        status: this.digitalFormComponent.status
                    };

                    console.log('[DigitalForms][updateDigitalFormComponent] digitalFormComponentDto = ', digitalFormComponentDto);

                    const response = await DigitalFormService.updateDigitalFormComponent(this.digitalFormComponent.id, digitalFormComponentDto);
                    console.log('[DigitalForms][updateDigitalFormComponent] Response = ', response);

                    const digitalFormsData = await DigitalFormService.getDigitalForms(this.profile.companyId);
                    this.userStore.setDigitalForms(digitalFormsData.data);

                    this.digitalForms = this.userStore.digitalForms;
                    this.originalDigitalForms = this.userStore.digitalForms;
                    console.log('[DigitalForms][updateDigitalFormComponent] this.digitalForms = ', this.digitalForms);

                    this.digitalForm = this.digitalForms.find(digitalForm => digitalForm.id === digitalFormId);
                    this.digitalFormComponents = this.digitalForm.formComponent;
                    console.log('[DigitalForms][updateDigitalFormComponent] this.digitalForm = ', this.digitalForm);
                    console.log('[DigitalForms][updateDigitalFormComponent] this.digitalFormComponents = ', this.digitalFormComponents);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated digital form component',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[DigitalForms][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update digital form component. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 5;
            this.submitted = false;
        },
        async deleteDigitalFormComponent() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.digitalFormComponent.id) {

                    try {
                        var digitalFormId = this.digitalForm.id;

                        const response = await DigitalFormService.deleteDigitalFormComponent(this.digitalFormComponent.id);
                        console.log('[DigitalForms][deleteDigitalFormComponent] Response = ', response);

                        const digitalFormsData = await DigitalFormService.getDigitalForms(this.profile.companyId);
                        this.userStore.setDigitalForms(digitalFormsData.data);

                        this.digitalForms = this.userStore.digitalForms;
                        this.originalDigitalForms = this.userStore.digitalForms;
                        console.log('[DigitalForms][deleteDigitalFormComponent] this.digitalForms = ', this.digitalForms);

                        this.digitalForm = this.digitalForms.find(digitalForm => digitalForm.id === digitalFormId);
                        this.digitalFormComponents = this.digitalForm.formComponent;
                        console.log('[DigitalForms][deleteDigitalFormComponent] this.digitalForm = ', this.digitalForm);
                        console.log('[DigitalForms][deleteDigitalFormComponent] this.digitalFormComponents = ', this.digitalFormComponents);

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted digital form component',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[DigitalForms][deleteDigitalFormComponent] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete digital form component. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 5;
            this.submitted = false;
        },
        async deleteDigitalFormResponse() {
            this.submitted = true;

            console.log('[DigitalForms][deleteDigitalFormResponse] this.digitalFormUniqueResponse = ', this.digitalFormUniqueResponse);

            if (this.confirmationResponse.trim() === "Y") {

                if (this.digitalFormUniqueResponse.uniqueId) {

                    try {
                        const response = await DigitalFormService.deleteDigitalFormResponse(this.digitalFormUniqueResponse.uniqueId);
                        console.log('[DigitalForms][deleteDigitalFormResponse] Response = ', response);

                        const [digitalFormsData, resultsData] = await Promise.all([
                            DigitalFormService.getDigitalForms(this.profile.companyId),
                            DigitalFormService.getUniqueResponses(this.digitalForm.id)
                        ]);

                        this.userStore.setDigitalForms(digitalFormsData.data);
                        this.digitalForms = this.userStore.digitalForms;
                        this.originalDigitalForms = this.userStore.digitalForms;
                        console.log('[DigitalForms][deleteDigitalFormResponse] this.digitalForms = ', this.digitalForms);

                        this.digitalFormUniqueResponses = resultsData.data;
                        console.log('[DigitalForms][deleteDigitalFormResponse] this.digitalFormUniqueResponses = ', this.digitalFormUniqueResponses);

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted digital form response',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[DigitalForms][deleteDigitalFormComponent] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete digital form response. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 9;
            this.submitted = false;
        }
    },
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge, .type-badge, .required-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-A {
        background: #9da5d9;
        color: #080c61;
    }

    &.status-I {
        background: #B3E5FC;
        color: #23547B;
    }

}

.type-badge {
    &.status-C {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-P {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-G {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-S {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-B {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-N {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-D {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-H {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-L {
        background: #d9b78a;
        color: #b94f03;
    }

    &.status-T {
        background: #b9a1de;
        color: #830293;
    }

    &.status-Y {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-M {
        background: #c90be5;
        color: #080c61;
    }

}

.required-badge {
    &.status-Y {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-N {
        background: #C8E6C9;
        color: #256029;
    }

}


.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

</style>
